import { useState, useEffect } from "react";
import { Download, Share, PlusSquare, X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { Capacitor } from "@capacitor/core";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: Array<string>;
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export function PWAInstallPrompt() {
  const isNativePlatform = Capacitor.isNativePlatform();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    if (isNativePlatform) return;

    // Check if already in standalone mode
    if (window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone) {
      setIsStandalone(true);
      return;
    }

    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    const handler = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show the install button if it hasn't been dismissed in this session
      const dismissed = sessionStorage.getItem("pwa-prompt-dismissed");
      if (!dismissed) {
        setIsVisible(true);
      }
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Support for manual trigger
    const showHandler = () => {
      setIsVisible(true);
    };
    window.addEventListener("aql-show-pwa-prompt", showHandler);

    // If it's iOS and not standalone, show the prompt after a short delay
    if (isIOSDevice && !isStandalone) {
      const dismissed = sessionStorage.getItem("pwa-prompt-dismissed");
      if (!dismissed) {
        const timer = setTimeout(() => setIsVisible(true), 3000);
        return () => clearTimeout(timer);
      }
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("aql-show-pwa-prompt", showHandler);
    };
  }, [isNativePlatform, isStandalone]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    // Show the install prompt
    await deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to the install prompt: ${outcome}`);
    
    // We've used the prompt, and can't use it again, throw it away
    setDeferredPrompt(null);
    setIsVisible(false);
  };

  const dismissPrompt = () => {
    setIsVisible(false);
    sessionStorage.setItem("pwa-prompt-dismissed", "true");
  };

  const [appName, setAppName] = useState("AQL APPS");

  useEffect(() => {
    if (isNativePlatform) return;

    // Detect app name from URL or context
    const params = new URLSearchParams(window.location.search);
    const app = params.get("app");
    const nameMap: Record<string, string> = {
      "tasks": "AQL Tasks",
      "vision": "AQL Vision",
      "clock": "AQL Clock",
      "fisio": "AQL Fisio",
      "tension": "AQL Tension",
      "meet": "AQL Meet"
    };
    if (app && nameMap[app]) setAppName(nameMap[app]);
  }, [isNativePlatform]);

  if (isNativePlatform || isStandalone || !isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="fixed bottom-24 left-4 right-4 z-[100] sm:left-auto sm:right-4 sm:w-80"
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-5 overflow-hidden relative">
          <button 
            onClick={dismissPrompt}
            className="absolute top-3 right-3 p-1 hover:bg-slate-100 rounded-full text-slate-400 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-start gap-4">
            <div className="bg-blue-600 p-2.5 rounded-xl text-white shrink-0 shadow-lg shadow-blue-100">
              <Download className="w-5 h-5" />
            </div>
            <div className="flex-1 pr-4">
              <h3 className="font-bold text-slate-900 text-sm leading-tight mb-1">
                Instalar {appName}
              </h3>
              <p className="text-xs text-slate-500 leading-relaxed mb-4">
                {isIOS 
                  ? "Adicione à sua tela inicial para acesso rápido e melhor experiência." 
                  : "Instale a aplicação no seu dispositivo para usar como uma app nativa."}
              </p>

              {isIOS ? (
                <div className="space-y-3 bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <div className="flex items-center gap-3 text-[11px] font-medium text-slate-600">
                    <div className="bg-white p-1 rounded border border-slate-200">
                      <Share className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <span>1. Toque no botão Partilhar</span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] font-medium text-slate-600">
                    <div className="bg-white p-1 rounded border border-slate-200">
                      <PlusSquare className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <span>2. Selecione "Ecrã Principal"</span>
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleInstallClick}
                  disabled={!deferredPrompt}
                  className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-md active:scale-95"
                >
                  Instalar Agora
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
